import { CurrencyPipe } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ValueFormatterParams } from 'ag-grid-community';
import { ColDef } from 'ag-grid-enterprise';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { SecurityHolding } from '@models/shares/security-holding';
import { SecurityRegistryRecord } from '@models/shares/security-registry-record';
import { CheckboxComponent } from '@shared/controls/checkbox/checkbox.component';
import { DividerComponent } from '@shared/components/commons/divider/divider.component';
import { InputNumberComponent } from '@shared/controls/input-number/input-number.component';
import { LoaderComponent } from '@shared/components/commons/loader/loader.component';
import { TextareaComponent } from '@shared/controls/textarea/textarea.component';
import { BaseShareFormComponent } from '../base-share-form/base-share-form.component';
import {
  ShareEstimateTableComponent
} from '../../../reusable-form-parts/share-estimate-table/share-estimate-table.component';
import {
  CompanySecurityConsolidationSubdivision,
  IShareTypesDistributionEstimate,
  resolutionSubjects,
  SubdivisionSteps,
  SubdivisionStepsEnum
} from '../../../models/shares/company-security-consolidation-subdivision.model';
import { setControlDisabled } from "../../../../shared/utils/set-control-disabled";
import { SecurityType } from "@models/shares/security-type";
import {
  CompanySecurityConsolidationSubdivisionBlockComponent,
  IShareSubdivisionConversionBlockFormControls
} from "../company-security-consolidation-subdivision-block/company-security-consolidation-subdivision-block.component";
import { debounceTime, merge } from "rxjs";
import { DatepickerHelper } from "../../../../shared/validators/date-picker-form-validators";
import { reloadShareholdersDebounceTimeMs } from "../constants/reload-shareholders-delay";
import { StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";

export interface IShareSubdivisionConversionFormControls extends IShareSubdivisionConversionBlockFormControls {
  resolutionText: FormControl<string | null>;
}

@Component({
  selector: 'app-share-subdivision-conversion',
  standalone: true,
  imports: [
    StepperFormComponent,
    ReactiveFormsModule,
    CheckboxComponent,
    DividerComponent,
    InputNumberComponent,
    TextareaComponent,
    ShareEstimateTableComponent,
    LoaderComponent,
    StepperFormDescriptionComponent,
    CompanySecurityConsolidationSubdivisionBlockComponent,
    ListGridClientSideComponent
  ],
  templateUrl: 'company-security-consolidation-subdivision.component.html',
  styleUrls: [
    '../company-security-cancellation/company-security-cancellation.component.scss',
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss'
  ]
})
export class CompanySecurityConsolidationSubdivisionComponent extends BaseShareFormComponent<SubdivisionStepsEnum, CompanySecurityConsolidationSubdivision> {
  @ViewChild(CompanySecurityConsolidationSubdivisionBlockComponent) shareSubdivisionConversionBlock!: CompanySecurityConsolidationSubdivisionBlockComponent;

  currencyPipe = inject(CurrencyPipe);

  override steps = SubdivisionSteps;
  override readonly StepsEnum = SubdivisionStepsEnum;
  readonly resolutionSubjects = resolutionSubjects;

  // Subdivision Conversion Consolidation
  securityRegistryRecordsHoldings: SecurityHolding[] = [];
  selectedSecurityHoldingClass: SecurityHolding | null = null;

  // estimate
  shareClassesDistributionEstimate: IShareTypesDistributionEstimate[] = [];
  shareTypesDistributionEstimate: IShareTypesDistributionEstimate[] = [];

  override stepperForm = new FormGroup({
    [SubdivisionStepsEnum.FormDescription]: new FormGroup({}),
    [SubdivisionStepsEnum.ResolutionSubject]: new FormGroup({
      alterationToRights: new FormControl<boolean>(false),
      convertShares: new FormControl<boolean>(true),
      callsLimitedExternallyAdministered: new FormControl<boolean>(false),
      reductionInShareCapital: new FormControl<boolean>(false),
      finAssistanceApprovalByCompanysOwnShareholders: new FormControl<boolean>(false),
      finAssistanceApprovalByShareholdersListedHoldingCompany: new FormControl<boolean>(false),
      finAssistanceApprovalByShareholdersOfUltimateHoldingCompany: new FormControl<boolean>(false),
      otherSubject: new FormControl<boolean>(false),
      sectionNumber: new FormControl<string | null>(null),
      briefDescription: new FormControl<string | null>(null),
    }),
    [SubdivisionStepsEnum.SubdivisionConversionConsolidation]: new FormGroup<IShareSubdivisionConversionFormControls>({
      ...CompanySecurityConsolidationSubdivisionBlockComponent.defineFormControls(),
      resolutionText: new FormControl<string | null>(null, [Validators.required]),
    }),
    [SubdivisionStepsEnum.Estimate]: new FormGroup({}),
  });

  constructor() {
    super();
    this.setCurrentStep(0);
    this.disableFirstStepCheckboxes();
    this.redirectAfterSubmit = false;
  }

  override setupFormChangeListeners() {
    super.setupFormChangeListeners();
    this.listenSubdivisionDateChanges();
    this.listenShareholderDetailsFormChange();
    this.listenSubdivisionConversionConversionFormChange();
  }

  override setCurrentStep(newStepIndex: number): void {
    this.currentStep = this.steps[newStepIndex].step;
    this.currentStepIndex = newStepIndex;

    if (this.currentStep === this.StepsEnum.Estimate && this.shouldLoadEstimate) {
      this.loadEstimates();
    }
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanySecurityConsolidationSubdivision);
  }

  override setupChange(change: CompanySecurityConsolidationSubdivision = this.formModel): void {
    if (!this.isEdit && change === this.formModel) {
      return;
    }

    this.resolutionSubjectForm.patchValue(change as any);
    this.subdivisionConversionConsolidationForm.patchValue(change as any);
    const selectedClass = Object.keys(change.modifiedClasses)?.[0] ?? '';

    if (selectedClass) {
      const securityHoldingClass = this.securityRegistryRecordsHoldings
        .find(holding => holding.securityType.class === selectedClass)
        ?.securityHoldingId ?? null;

      this.subdivisionConversionConsolidationForm.patchValue({
        securityHoldingClass,
        subdivisionDate: change.changeDate,
        newNumberShares: change.modifiedClasses[selectedClass],
        resolutionText: change.resolutionText
      });
    }

    this.disableFirstStepCheckboxes();
  }

  override buildDocument(): Document | null {
    const companySecurityConsolidationSubdivision = this.buildShareFormChange();

    try {
      return new Document({
        changes: [companySecurityConsolidationSubdivision],
        entityId: this.companyChangeData?.entityId,
        type: 'c:2205',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  override buildShareFormChange(): CompanySecurityConsolidationSubdivision {
    const partialChange = this.shareSubdivisionConversionBlock.buildShareFormChange();
    const changeDate = DatepickerHelper.buildDateString(this.subdivisionConversionConsolidationForm.controls.subdivisionDate.value!);

    return new CompanySecurityConsolidationSubdivision({
      ...partialChange,
      ...this.resolutionSubjectForm.value as Partial<CompanySecurityConsolidationSubdivision>,
      changeDate,
      dateOfResolution: changeDate
    });
  }

  override onShareholdersLoadSuccess(records: SecurityRegistryRecord[], isReload = false) {
    this.securityRegistryRecords = records;
    this.isLoading = false;

    const holdingsMap = new Map<string, SecurityHolding[]>();
    this.securityRegistryRecords
      .flatMap(record => record.holdings)
      .forEach(holding => {
        const securityTypeClass = holding.securityType.class;

        if (!holdingsMap.has(securityTypeClass)) {
          holdingsMap.set(securityTypeClass, [holding]);
        } else {
          holdingsMap.get(securityTypeClass)?.push(holding);
        }
      });

    this.securityRegistryRecordsHoldings = Array.from(holdingsMap.values())
      .map(holdings => {
        return new SecurityHolding({
          number: holdings.reduce((sum, holding) => sum + holding.number, 0),
          paid: holdings.reduce((sum, holding) => sum + holding.paid, 0),
          unpaid: holdings.reduce((sum, holding) => sum + holding.unpaid, 0),
          securityTypeId: holdings[0].securityType.securityTypeId,
          securityType: holdings[0].securityType,
          securityHoldingId: holdings[0].securityType.class
        });
      });

    this.cdr.detectChanges();

    if (!isReload) {
      this.setupChange();
    }
  }

  override onEstimateLoadSuccess(estimatedSecurityRegistryRecords: SecurityRegistryRecord[]) {
    this.estimatedSecurityRegistryRecords = estimatedSecurityRegistryRecords
      .filter(record => record?.holders.length > 0);

    const allHoldings = estimatedSecurityRegistryRecords.flatMap(record => record.holdings);

    // Share Classes Distribution Estimate
    this.shareClassesDistributionEstimate = Array.from(allHoldings
      .reduce((classes, holding) => {
        const securityTypeClass = holding.securityType.class;

        if (!classes.has(securityTypeClass)) {
          classes.set(securityTypeClass, {
            class: securityTypeClass,
            identifier: '',
            numberOfShares: 0,
            nominalPrice: 0,
            nominalUnpaid: 0,
            totalPaid: 0,
            totalUnpaid: 0
          });
        }

        const record = classes.get(securityTypeClass)!;

        record.numberOfShares += holding.number;
        record.totalPaid += holding.paid;
        record.totalUnpaid += holding.unpaid;

        return classes;
      }, new Map<string, IShareTypesDistributionEstimate>()).values());

    // Share Types Distribution Estimate
    this.shareTypesDistributionEstimate = Array.from(allHoldings
      .reduce((classes, holding) => {
        const securityTypeId = holding.securityType.securityTypeId;

        if (!classes.has(securityTypeId)) {
          classes.set(securityTypeId, {
            class: '',
            identifier: SecurityType.buildIdentifier(holding.securityType),
            numberOfShares: 0,
            nominalPrice: 0,
            nominalUnpaid: 0,
            totalPaid: 0,
            totalUnpaid: 0
          });
        }

        const record = classes.get(securityTypeId)!;

        record.numberOfShares += holding.number;
        record.nominalPrice += holding.number * holding.securityType.nominalPrice;
        record.nominalUnpaid += holding.number * holding.unpaid;
        record.totalPaid += holding.paid;
        record.totalUnpaid += holding.unpaid;

        return classes;
      }, new Map<string, IShareTypesDistributionEstimate>()).values());
  }

  private listenSubdivisionDateChanges(): void {
    this.subdivisionConversionConsolidationForm.controls.subdivisionDate.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(reloadShareholdersDebounceTimeMs),
      )
      .subscribe((transactionDate) => {
        if (!transactionDate) {
          return;
        }

        this.loadShareholders(transactionDate, true);
      });
  }

  private listenSubdivisionConversionConversionFormChange(): void {
    merge(
      this.subdivisionConversionConsolidationForm.controls.newNumberShares.valueChanges,
      this.subdivisionConversionConsolidationForm.controls.subdivisionDate.valueChanges,
      this.subdivisionConversionConsolidationForm.controls.securityHoldingClass.valueChanges,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fillResolutionText());
  }

  private fillResolutionText(): void {
    const numberOfShares = this.subdivisionConversionConsolidationForm.controls.newNumberShares.value ?? 0;
    const isConsolidation = (this.selectedSecurityHoldingClass?.number ?? 0) > numberOfShares;
    const changeType1 = isConsolidation ? 'consolidate' : 'subdivide';
    const changeType2 = isConsolidation ? 'consolidation' : 'subdivision';
    const shareClass = this.subdivisionConversionConsolidationForm.controls.securityHoldingClass.value;
    const date = DatepickerHelper.toString(this.subdivisionConversionConsolidationForm.controls.subdivisionDate.value);

    const resolutionText = `It was resolved that the company shall ${ changeType1 } its ${ shareClass } shares effective ${ date }. The new number of shares after ${ changeType2 } is ${ numberOfShares } ${ shareClass } shares.`;
    this.subdivisionConversionConsolidationForm.controls.resolutionText.setValue(resolutionText);
  }

  private listenShareholderDetailsFormChange(): void {
    this.stepperForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.shouldLoadEstimate = true);
  }

  private disableFirstStepCheckboxes(): void {
    this.resolutionSubjects.forEach((subject) => setControlDisabled(this.resolutionSubjectForm.controls[subject.key]));
  }

  private toCurrency(value: number) {
    return this.currencyPipe.transform(value ?? '', '$', 'code', '1.2-2')!;
  }

  get resolutionSubjectForm() {
    return this.stepperForm.controls[SubdivisionStepsEnum.ResolutionSubject];
  }

  get subdivisionConversionConsolidationForm() {
    return this.stepperForm.controls[SubdivisionStepsEnum.SubdivisionConversionConsolidation];
  }

  readonly shareClassesDistributionEstimateConfig: ColDef[] = [
    {
      headerName: 'Share Class',
      field: 'class',
      flex: 1,
      autoHeight: true,
      cellClass: 'overflow-hidden pr-0',
      suppressHeaderMenuButton: true,
      minWidth: 200,
    },
    {
      headerName: 'Number of Shares',
      field: 'numberOfShares',
      autoHeight: true,
      cellClass: 'overflow-hidden text-end',
      suppressHeaderMenuButton: true,
    },
    {
      headerName: 'Total Paid',
      field: 'totalPaid',
      autoHeight: true,
      cellClass: 'overflow-hidden text-end',
      suppressHeaderMenuButton: true,
      valueFormatter: (params: ValueFormatterParams<unknown, string>) => {
        return this.currencyPipe.transform(params.value ?? '', '$', 'code', '1.2-2')!;
      },
    },
    {
      headerName: 'Total Unpaid',
      field: 'totalUnpaid',
      autoHeight: true,
      cellClass: 'overflow-hidden text-end',
      suppressHeaderMenuButton: true,
      valueFormatter: (params: ValueFormatterParams<unknown, string>) => {
        return this.currencyPipe.transform(params.value ?? '', '$', 'code', '1.2-2')!;
      },
    }
  ];

  readonly shareTypesDistributionEstimateConfig: ColDef[] = [
    {
      headerName: 'Share Type',
      field: 'identifier',
      flex: 1,
      autoHeight: true,
      cellClass: 'overflow-hidden pr-0',
      suppressHeaderMenuButton: true,
      minWidth: 200
    },
    {
      headerName: 'Number of Shares',
      field: 'numberOfShares',
      autoHeight: true,
      cellClass: 'overflow-hidden text-end',
      suppressHeaderMenuButton: true,
    },
    {
      headerName: 'Paid',
      field: 'totalPaid',
      autoHeight: true,
      cellClass: 'overflow-hidden text-end',
      suppressHeaderMenuButton: true,
      valueFormatter: (params: ValueFormatterParams<unknown, string>) => {
        return this.toCurrency(Number(params.value) ?? 0);
      },
    },
    {
      headerName: 'Unpaid',
      field: 'totalUnpaid',
      autoHeight: true,
      cellClass: 'overflow-hidden text-end',
      suppressHeaderMenuButton: true,
      valueFormatter: (params: ValueFormatterParams<unknown, string>) => {
        return this.toCurrency(Number(params.value) ?? 0);
      },
    },
  ];
}
